/**
 * #1030 — native full-game Polymarket US totals.
 *
 * Explicit families: `football_team_full_game_total` (NFL and CFB) and
 * `basketball_team_full_game_total` (NBA). Despite the `team` token in the venue's enum, each native contract is
 * the COMBINED game total: YES settles when both teams together score more
 * than the native line, overtime included. Its native sides are "Over" (the
 * YES / long side) and "Under" (NO). Period totals (halves, quarters), the
 * selected-team points total (`football_team_points_full_game_total`) and
 * every other family stay unsupported: a family must be listed here to join.
 *
 * Exact identities only. A total is chosen by canonical game key, this family,
 * full-game period and a numeric threshold equal to the board line; nothing is
 * read from a title, slug or ticker suffix, and no nearby strike is used.
 */
import type { UsDiscoveryMarket } from './marketClient';
import type { UsGameSport } from './sports';

export const US_FULL_GAME_TOTAL_FAMILIES = Object.freeze({
  football_team_full_game_total: { sports: ['NFL', 'CFB'] as readonly UsGameSport[], unit: 'points',
    yesLabel: 'Over', noLabel: 'Under' },
  basketball_team_full_game_total: { sports: ['NBA'] as readonly UsGameSport[], unit: 'points',
    yesLabel: 'Over', noLabel: 'Under' },
} as const);
export type UsFullGameTotalFamily = keyof typeof US_FULL_GAME_TOTAL_FAMILIES;

/** The supported family for this exact type and sport, or null. */
export function usFullGameTotalFamily(type: unknown, sport: unknown) {
  if (typeof type !== 'string' || !Object.prototype.hasOwnProperty.call(US_FULL_GAME_TOTAL_FAMILIES, type)) return null;
  const family = US_FULL_GAME_TOTAL_FAMILIES[type as UsFullGameTotalFamily];
  return family.sports.some((s) => s === sport) ? family : null;
}

/** The family a board game of this US sport would join, or null. */
export function usFullGameTotalFamilyForSport(sport: UsGameSport | null): UsFullGameTotalFamily | null {
  if (!sport) return null;
  for (const [type, family] of Object.entries(US_FULL_GAME_TOTAL_FAMILIES)) {
    if (family.sports.includes(sport)) return type as UsFullGameTotalFamily;
  }
  return null;
}

/** A board threshold the US join can compare exactly: finite, positive, at
 * most four integer digits and two decimals (the catalogue's strike grammar). */
export function validUsTotalThreshold(value: unknown): value is number {
  return typeof value === 'number' && Number.isFinite(value) && value > 0
    && /^\d{1,4}(?:\.\d{1,2})?$/.test(String(value));
}

/** A native line string in the same grammar, or null. */
function nativeThreshold(line: string | null): number | null {
  return line !== null && /^\d{1,4}(?:\.\d{1,2})?$/.test(line) && Number(line) > 0 ? Number(line) : null;
}

/** An exact native full-game total, as a saved order or holding names it. */
export interface UsFullGameTotalTarget {
  marketSlug: string; marketId: string; family: UsFullGameTotalFamily; line: string;
}

/** The exact supported full-game total this catalogue row is, or null. */
/** The fields the total joins read: a catalogue row, or #1231's live line quote. */
export type UsTotalCandidate = Pick<UsDiscoveryMarket, 'marketSlug' | 'marketId' | 'sport' | 'gameKey' | 'yesTeam'
  | 'marketType' | 'sportsMarketType' | 'period' | 'line' | 'playerProp'>;

export function usFullGameTotalTarget(m: UsTotalCandidate): UsFullGameTotalTarget | null {
  if (!usFullGameTotalFamily(m.sportsMarketType, m.sport) || m.marketType !== 'totals' || m.period !== 'full'
    || m.yesTeam !== null || m.playerProp != null || nativeThreshold(m.line) === null
    || !/^[A-Za-z0-9_-]{1,256}$/.test(m.marketSlug) || !/^[0-9]{1,32}$/.test(m.marketId)) return null;
  return { marketSlug: m.marketSlug, marketId: m.marketId, family: m.sportsMarketType as UsFullGameTotalFamily, line: m.line! };
}

export type UsTotalPick<M = UsDiscoveryMarket> =
  | { kind: 'market'; market: M }
  | { kind: 'none' }
  | { kind: 'ambiguous'; count: number };

/**
 * The single native full-game total for this game, family and threshold.
 *
 * A row of the requested family for this game whose shape or line cannot be
 * read exactly could be the same contract, so it makes the set ambiguous
 * rather than being skipped. Two exact candidates are refused, never ranked.
 * Rows at another strike, another family, a period or another game are not
 * candidates at all.
 */
export function pickUsFullGameTotal<M extends UsTotalCandidate = UsDiscoveryMarket>(markets: readonly M[], usGameKey: string,
  target: { family: UsFullGameTotalFamily; threshold: number }): UsTotalPick<M> {
  if (!validUsTotalThreshold(target.threshold)) return { kind: 'none' };
  const exact: M[] = [];
  let unreadable = 0;
  for (const m of markets) {
    if (m.gameKey !== usGameKey || m.sportsMarketType !== target.family) continue;
    if (!usFullGameTotalTarget(m)) { unreadable++; continue; }
    if (nativeThreshold(m.line) === target.threshold) exact.push(m);
  }
  if (unreadable > 0) return { kind: 'ambiguous', count: exact.length + unreadable };
  if (exact.length === 0) return { kind: 'none' };
  if (exact.length > 1) return { kind: 'ambiguous', count: exact.length };
  return { kind: 'market', market: exact[0] };
}

/** Which native outcome a board "Over/Under — YES/NO" selection buys. Over
 * YES is native YES; Under YES is native NO; each NO is the complement. */
export function usTotalOutcome(side: 'over' | 'under', boardSide: 'yes' | 'no'): 'yes' | 'no' {
  return (side === 'over') === (boardSide === 'yes') ? 'yes' : 'no';
}
